import { useEffect } from 'react';
import { usePageSEO } from '@/hooks/usePageSEO';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import HeroArticle from './components/HeroArticle';
import TableMatieres from './components/TableMatieres';
import SectionIntro from './components/SectionIntro';
import PourquoiTot from './components/PourquoiTot';
import Signes from './components/Signes';
import VerificationsExterieures from './components/VerificationsExterieures';
import VerificationsEntretoit from './components/VerificationsEntretoit';
import UrgentVsPlanifiable from './components/UrgentVsPlanifiable';
import InspectionPro from './components/InspectionPro';
import FAQSignes from './components/FAQSignes';
import CTAFinal from './components/CTAFinal';
import ArticlesLies from './components/ArticlesLies';

function setMeta(name: string, content: string) {
  let meta = document.querySelector(`meta[name="${name}"]`);
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute('name', name);
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', content);
}

function removeOldLdJson() {
  const existing = document.querySelectorAll('script[type="application/ld+json"]');
  existing.forEach((el) => {
    if (el.id === 'blogsignes-ld') el.remove();
  });
}

export default function BlogSignesPage() {
  usePageSEO({
    title: "5 signes que votre toiture est à refaire | Lévis",
    description: "Bardeaux soulevés, infiltrations, mousse ? Découvrez les 5 signes qui montrent que votre toiture à Lévis doit être refaite. Guide d'inspection.",
    urlPath: "/blog/signes-toiture-a-refaire",
    keywords: "signes toiture refaire, quand refaire toiture Lévis, bardeau usé, infiltration toiture, mousse toiture",
    ogImage: '/og/og-blog-signes-toiture-a-refaire.jpg',
    ogImageAlt: '5 signes que votre toiture est à refaire à Lévis - Quand refaire votre toiture',
    jsonLdSchema: {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Accueil', item: 'https://couvreur-levis.com/' },
        { '@type': 'ListItem', position: 2, name: 'Blog', item: 'https://couvreur-levis.com/blog' },
        { '@type': 'ListItem', position: 3, name: 'Signes toiture à refaire', item: 'https://couvreur-levis.com/blog/signes-toiture-a-refaire' },
      ],
    },
  });

  useEffect(() => {
    document.title = "5 signes que votre toiture est à refaire | Lévis";
    setMeta('description', "Bardeaux soulevés, infiltrations, mousse ? Découvrez les 5 signes qui montrent que votre toiture à Lévis doit être refaite. Guide d'inspection.");
    setMeta('keywords', 'signes toiture refaire, quand refaire toiture Lévis, bardeau usé, infiltration toiture, mousse toiture');

    removeOldLdJson();

    const ld = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Article",
          "@id": "https://couvreur-levis.com/blog/signes-toiture-a-refaire/#article",
          headline: "5 signes que votre toiture est à refaire à Lévis",
          description: "Guide diagnostic avec 5 signes visibles pour savoir quand refaire sa toiture à Lévis.",
          image: "https://readdy.ai/api/search-image?query=Dramatic%20close-up%20of%20severely%20damaged%20residential%20roof%20with%20lifted%20curled%20asphalt%20shingles%2C%20green%20moss%20growth%20between%20cracks%2C%20dark%20water%20stains%20visible%20on%20wood%20decking%20underneath%2C%20overcast%20gloomy%20sky%20creating%20moody%20atmosphere%2C%20warning%20signs%20of%20aging%20roof%20needing%20replacement%2C%20deep%20charcoal%20and%20dark%20green%20tones%20with%20burnt%20orange%20safety%20cone%20in%20foreground%2C%20photorealistic%20documentary%20photography%20with%20urgent%20repair%20need%20aesthetic&width=1200&height=630&seq=og-blogsignes&orientation=landscape",
          author: {
            "@type": "Organization",
            name: "Couvreur-Lévis.com",
            url: "https://couvreur-levis.com",
          },
          publisher: {
            "@type": "Organization",
            name: "Couvreur-Lévis.com",
            logo: {
              "@type": "ImageObject",
              url: "https://couvreur-levis.com/og-default.jpg",
            },
          },
          datePublished: "2026-05-16",
          dateModified: "2026-05-16",
          mainEntityOfPage: {
            "@type": "WebPage",
            "@id": "https://couvreur-levis.com/blog/signes-toiture-a-refaire",
          },
        },
        {
          "@type": "HowTo",
          "@id": "https://couvreur-levis.com/blog/signes-toiture-a-refaire/#howto",
          name: "Comment inspecter sa toiture soi-même",
          description: "Méthode en 2 étapes pour inspecter une toiture du sol et de l'entretoit.",
          totalTime: "PT15M",
          step: [
            { "@type": "HowToStep", name: "Inspection extérieure", text: "Du sol avec jumelles, vérifiez les bardeaux soulevés, la décoloration, les solins, la mousse et les affaissements." },
            { "@type": "HowToStep", name: "Inspection entretoit", text: "Avec une lampe de poche, vérifiez le bois humide, l'isolant compressé, la lumière à travers le pontage et la moisissure." },
          ],
        },
        {
          "@type": "FAQPage",
          "@id": "https://couvreur-levis.com/blog/signes-toiture-a-refaire/#faq",
          mainEntity: [
            {
              "@type": "Question",
              name: "Combien de temps dure un bardeau d'asphalte à Lévis ?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Entre 20 et 25 ans pour un bardeau standard, 25 à 35 ans pour un bardeau architectural de qualité.",
              },
            },
            {
              "@type": "Question",
              name: "Réparer une section ou tout refaire ?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Si le bardeau a moins de 15 ans et qu'une seule section est endommagée, une réparation localisée peut suffire. Sinon, la réfection complète est plus économique à long terme.",
              },
            },
            {
              "@type": "Question",
              name: "Comment savoir l'âge exact de ma toiture ?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Vérifiez les documents de la transaction immobilière ou faites estimer par un couvreur.",
              },
            },
            {
              "@type": "Question",
              name: "Mon assurance habitation couvre-t-elle une toiture usée ?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Non. L'assurance couvre les dommages soudains causés par un sinistre. L'usure normale n'est pas couverte.",
              },
            },
            {
              "@type": "Question",
              name: "Puis-je enlever la mousse moi-même ?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Oui avec un mélange eau-vinaigre, mais ne montez pas sur le toit sans équipement. La solution professionnelle est préférable.",
              },
            },
          ],
        },
      ],
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'blogsignes-ld';
    script.textContent = JSON.stringify(ld);
    document.head.appendChild(script);

    window.scrollTo(0, 0);

    return () => {
      removeOldLdJson();
    };
  }, []);

  return (
    <div className="font-inter min-h-screen bg-white">
      <Header />
      <main>
        <HeroArticle />

        <div className="max-w-6xl mx-auto px-4 md:px-6 lg:px-10 pb-16 md:pb-24">
          <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
            <TableMatieres />

            <div className="flex-1 min-w-0 space-y-14 md:space-y-20">
              <SectionIntro />
              <PourquoiTot />
              <Signes />
              <VerificationsExterieures />
              <VerificationsEntretoit />
              <UrgentVsPlanifiable />
              <InspectionPro />
              <FAQSignes />
            </div>
          </div>
        </div>

        <CTAFinal />
        <ArticlesLies />
      </main>
      <Footer />
    </div>
  );
}